import { OrgChartRow, TreeNode } from "./organization-chart";
import { NodeStateService } from "./node-state-service";
import { Txt } from "../common/text";

export class TreeRenderer
{
    constructor(private nodeStateService: NodeStateService)
    {
    }

    buildTreeStructure(filteredData: OrgChartRow[]): TreeNode[]
    {
        return this.buildTreeFromRows(filteredData);
    }

    createHorizontalTree(nodes: TreeNode[], level: number = 0): string
    {
        if (!nodes || nodes.length === 0)
        {
            return "";
        }

        let html = `<div class="tree-level" style=
                                "display: flex; flex-direction: column; margin-left: ${level * 200}px; padding-top: 1px">`;

        nodes.forEach(node =>
        {
            const isCollapsed = this.nodeStateService.isCollapsed(node.id);
            const hasChildren = node.children && node.children.length > 0;

            html += "<div class=\"tree-node-container\" style=\"display: flex; align-items: flex-start; margin: 5px 0;\">";
            html += this.createNodeHTML(node, isCollapsed, hasChildren);

            if (!isCollapsed && hasChildren)
            {
                html += "<div class=\"tree-children\" style=\"padding-left: 15px;\">";
                html += this.createHorizontalTree(node.children, 0);
                html += "</div>";
            }

            html += "</div>";
        });

        html += "</div>";
        return html;
    }

    private buildTreeFromRows(rows: OrgChartRow[]): TreeNode[]
    {
        const nodeMap = new Map<string, TreeNode>();
        const rootNodes: TreeNode[] = [];

        // Create all nodes
        for (const row of rows)
        {
            const node: TreeNode = {
                id: row.Node.Value,
                display: row.Tooltip,
                image: row.Image,
                tooltip: row.Tooltip,
                jobTitle: row.JobTitle,
                children: []
            };

            nodeMap.set(node.id, node);
        }

        // Build parent-child relationships
        for (const row of rows)
        {
            const nodeId = row.Node.Value;
            const managerId = row.ManagerId;

            if (!managerId)
            {
                const node = nodeMap.get(nodeId);
                if (node && !rootNodes.includes(node))
                {
                    rootNodes.push(node);
                }
            }
            else
            {
                const parentNode = nodeMap.get(managerId);
                const childNode = nodeMap.get(nodeId);

                if (parentNode && childNode)
                {
                    if (!parentNode.children.includes(childNode))
                    {
                        parentNode.children.push(childNode);
                    }
                }
            }
        }
        const sortNodes = (nodes: TreeNode[]): TreeNode[] =>
            nodes
                .map(n => ({ ...n, children: sortNodes(n.children) }))
                .sort((a, b) =>
                {
                    const aHasChildren = a.children.length > 0 ? 0 : 1;
                    const bHasChildren = b.children.length > 0 ? 0 : 1;
                    return aHasChildren - bHasChildren;
                });

        return sortNodes(rootNodes);
    }

    private createNodeHTML(node: TreeNode, isCollapsed: boolean, hasChildren: boolean): string
    {
        const imgSrc = node.image || "images/profile.png";

        const collapseIndicator = isCollapsed && hasChildren
            ? " <span style=\"color:#007bff; font-weight:bold;\">[+]</span>"
            : "";

        return `
        <div class="horizontal-org-node ${isCollapsed && hasChildren ? "collapsed" : ""}"
            data-node-id="${node.id}" 
            title="${node.tooltip || ""}">
            <div class="node-content" style="display: flex; align-items: center; flex-direction: row">
                <img src="${imgSrc}"
                    alt="${node.id}"
                    style="width: 32px; height: 32px; border-radius: 50%; object-fit: cover">
                
                <div class="node-text">
                    <div class="node-text-container">
                        <div class="node-name">${node.display}</div>
                        <div class="node-job">${node.jobTitle}</div>
                    </div>
                </div>
                ${collapseIndicator}
            </div>
        </div>
        `;
    }
}
